import {ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, inject, Input, Output, ViewChild} from '@angular/core';
import {ToastService} from '@paperlessio/sdk/api/util';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {AnimatedModalState} from '@shared/animated-modal/animated-modal-state';
import {SafeUrl} from '@angular/platform-browser';

@Component({
  selector: 'app-file-picker',
  templateUrl: './file-picker.component.html',
  styleUrls: ['./file-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('showHide', [
      state('visible',
        style({height: '*'})
      ),
      state('hidden',
        style({height: '0'})
      ),
      transition('visible <=> hidden', animate('0ms ease'))
    ])
  ]
})
export class FilePickerComponent {
  @Input() file: File | string | SafeUrl; // The actual file or the file url
  @Input() allowDrop = true;
  @Input() disabled = false;
  @Input() clickDisabled = false;
  @Input() dragDropOnly = false;
  @Input() mimeTypes: string[] = []; // application/json etc.
  @Input() formatDisplay: string[]; // jpg, png, pdf etc. - 'all' to allow everything
  @Input() maxFileSize = 100_000_000; // Bytes (100MB)
  @Input() appearance: 'area' | 'block' | 'button' | 'compact' = 'area';
  @Input() size: 'md' | 'lg' = 'md';
  @Input() translation = 'base.file_picker.defaults';
  @Input() flexColumn = false;
  @Input() animatedModalState = AnimatedModalState.visible;
  @Input() hideDropInfo = false;
  @Input() maxFiles: number | null = 1;

  @Output() fileChange = new EventEmitter<File>();

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  public dragFileValid = false;

  private toast = inject(ToastService);

  @HostListener('click', ['$event'])
  public clickFileInput(event?: MouseEvent) {
    if (!this.file
      && (
        (
          event != null
          && !(event.target as HTMLElement).isSameNode(this.fileInput.nativeElement)
        )
        || event == null
      )
      && this.fileInput
      && !this.disabled
      && !this.clickDisabled
      && this.appearance !== 'button'
      && this.appearance !== 'compact') {
      this.openFilePicker();
    }
  }

  public openFilePicker() {
    // When resetting the file upload input (for example) and re-selecting a file, the file input doesn't register the input, so we just clear the input here.
    this.fileInput.nativeElement.value = '';
    this.fileInput?.nativeElement?.click();
  }

  get multiple(): boolean {
    return this.maxFiles == null || this.maxFiles > 1;
  }

  setFile(files: File[]): void {
    if (files && files.length <= 0) {
      return;
    }

    if (this.maxFiles && files.length > this.maxFiles) {
      this.toast.error(this.translation + '.errors.max_files', {maxFiles: this.maxFiles});
      return;
    }

    Array.from(files).forEach(file => {
      if (!!file) {
        const sizeAllowed = !(file.size > this.maxFileSize);
        if (!sizeAllowed) {
          this.toast.error(this.translation + '.errors.file_size');
        }

        const formatAllowed = this.mimeTypes.length > 0 ? this.mimeTypes.find(f => file.type === f) : true;
        if (!formatAllowed) {
          this.toast.error(this.translation + '.errors.file_type');
        }

        if (sizeAllowed && formatAllowed) {
          this.file = file;
          this.fileChange.emit(file);
        }
      }
    });
  }

  checkDragFile(event: DragEvent) {
    this.dragFileValid = event.dataTransfer.types.includes('Files');
  }

  resetInput() {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
}
